<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import ReadinessScale from '@/components/ReadinessReport/ReadinessScale.vue'
import ReadinessReportSurvey from '@/components/ReadinessReport/ReadinessReportSurvey.vue'
import PasserInsights from '@/components/ReadinessReport/PasserInsights.vue'
import { buildUTMParamString } from '@/store/utils'
import { userModule } from '@/store/user/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { readinessModule } from '@/store/readiness/module'
import { analyticsModule } from '@/store/analytics/module'
import { screenModule } from '@/store/screen/module'
import type { Study } from '@pocketprep/types'

const route = useRoute()

const {
    ModalContainer,
    Modal,
    Link: PocketLink,
    Tag,
    Icon,
} = UIKit

const props = defineProps<{
    'variant': 'v2-generic-factors' | 'v3-passer-insights'
}>()

const emit = defineEmits<{
    'close': []
}>()

const isLoading = ref(true)
const readinessDetails = ref<ReturnType<Study.Cloud.fetchReadinessReportDetails> | null>(null)
const isReportLoadingFailed = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const isMobile = computed(() => {
    return breakpoint.value === 'black-bear'
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const currentExamName = computed(() => {
    return currentExamMetadata.value?.nativeAppName
})

const isPasserInsightReport = computed(() => {
    return props.variant === 'v3-passer-insights'
})

const readinessScoreScaled = computed(() => {
    return (readinessDetails.value?.readinessScoreRFNumeric || 0) * 10
})

const readinessLevel = computed(() => {
    return readinessDetails.value?.readinessScoreRFNonNumeric
})

const passingLikelihood = computed(() => {
    if (readinessLevel.value === 'Low') {
        return 'low'
    } else if (readinessLevel.value === 'Moderate') {
        return 'fair'
    } else if (readinessLevel.value === 'Good') {
        return 'good'
    } else if (readinessLevel.value === 'High') {
        return 'strong'
    } else {
        return 'unknown'
    }
})

const nudge = computed(() => {
    if (readinessLevel.value === 'Low') {
        if (isPasserInsightReport.value) {
            // eslint-disable-next-line max-len
            return '<strong>Your exam date is set, and you have a target to work toward!</strong> The data above shows where your energy is going and where there\'s room to grow. Your next step is to build momentum in the area that will move the needle most right now. You\'ve got this!'
        } else {
            // eslint-disable-next-line max-len
            return '<strong>Your exam date is set, and you have a target to work toward!</strong> The readiness factors below highlight what impacts your score the most. Your next step is to build momentum in the area that will move the needle most right now. You\'ve got this!'
        }
    } else if (readinessLevel.value === 'Moderate') {
        if (isPasserInsightReport.value) {
            // eslint-disable-next-line max-len
            return '<strong>Your study habits are taking shape!</strong> The comparison above highlights a few areas where closing the gap could make a significant difference in how prepared you feel on exam day. Now it\'s time to focus on the highest-impact place to direct your effort. We\'re rooting for you!'
        } else {
            // eslint-disable-next-line max-len
            return '<strong>Your study habits are taking shape!</strong> The readiness factors below highlight areas that could make a significant difference in how prepared you feel on exam day. Now it\'s time to focus on the highest-impact place to direct your effort. We\'re rooting for you!'
        }
    } else if (readinessLevel.value === 'Good') {
        if (isPasserInsightReport.value) {
            // eslint-disable-next-line max-len
            return '<strong>Your studying is paying off, and it shows.</strong> Use the comparison above to refine your study strategy in the final stretch before exam day. Keep up the great work!'
        } else {
            // eslint-disable-next-line max-len
            return '<strong>Your studying is paying off, and it shows.</strong> Use the readiness factors below to refine your study strategy in the final stretch before exam day. Keep up the great work!'
        }
    } else if (readinessLevel.value === 'High') {
        if (isPasserInsightReport.value) {
            // eslint-disable-next-line max-len
            return '<strong>You\'re in excellent shape, and you should feel genuinely confident about where you stand.</strong> Now is the time to maintain what\'s working and close the gaps on any remaining weaknesses. Success is within reach!'
        } else {
            // eslint-disable-next-line max-len
            return 'You\'re in excellent shape, and you should feel genuinely confident about where you stand. Now is the time to maintain what\'s working and close the gaps on any remaining weaknesses. Success is within reach!'
        }
    } else {
        return 'unknown'
    }
})

const factorCounts = computed(() => {
    return (readinessDetails.value?.features || []).reduce((acc, { influence }) => {
        if (influence === -1) {
            acc.low++
        } else if (influence === 0) {
            acc.neutral++
        } else {
            acc.high++
        }
        return acc
    }, { low: 0, neutral: 0, high: 0 })
})

onMounted(async () => {
    await userModule.actions.fetchUserData()

    try {
        readinessDetails.value = await readinessModule.actions.fetchReadinessReportDetails() || null
    } catch (err) {
        isReportLoadingFailed.value = true
    }

    isLoading.value = false

    setTimeout(() => {
        const titleEl: HTMLElement | null = document.querySelector('.readiness-report__title')
        titleEl?.focus()
    }, 0)
})

const aboutModelClicked = () => {
    const amplitudeReadinessProps = readinessModule.getters.getAmplitudeReadinessProps(route)
    analyticsModule.actions.amplitudeTrack('Readiness About Model Link Clicked', amplitudeReadinessProps)
}

</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="readinessReportModal"
            class="readiness-report"
            :is-dark-mode="isDarkMode"
            @close="emit('close')"
        >
            <template #modal>
                <Modal
                    :is-dark-mode="isDarkMode"
                    class="readiness-report__modal"
                    :show-close-button="true"
                    @close="emit('close')"
                >
                    <div
                        v-if="isMobile"
                        class="readiness-report__title-divider"
                        v-dark
                    />
                    <div tabindex="-1" class="readiness-report__title">
                        <span v-if="isPasserInsightReport">Passer Insights Report</span>
                        <span v-else>Exam Readiness Report</span>
                        <Tag
                            class="readiness-report__beta-tag"
                            :size="'large'"
                            :is-beta-tag="true"
                            :is-dark-mode="isDarkMode"
                        >
                            <template #tagMessage>
                                Beta
                            </template>
                        </Tag>
                    </div>
                    <template v-if="isLoading">
                        <Icon
                            type="loading2"
                            class="readiness-report__loading-spinner"
                            :is-dark-mode="isDarkMode"
                        />
                    </template>
                    <template v-else-if="isReportLoadingFailed">
                        <div class="readiness-report__error">
                            <Icon class="readiness-report__error-icon" type="warning" />
                            <div class="readiness-report__error-head">
                                Error loading report
                            </div>
                            <div class="readiness-report__error-description" v-dark>
                                Close and try again. If the error persists, please contact support.
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="readiness-report__content">
                            <template v-if="isPasserInsightReport">
                                <div class="readiness-report__insight-head">
                                    <div class="readiness-report__insight-title">
                                        How do your study behaviors compare to passers?
                                    </div>
                                    <div class="readiness-report__insight-description">
                                        We have compiled the study data of thousands of Pocket Prep users who
                                        <span class="readiness-report__highlight-container">
                                            passed
                                            <Icon
                                                type="highlight"
                                                class="readiness-report__highlight"
                                                v-dark
                                            />
                                        </span>
                                        their {{ currentExamName }} so you can study smarter for exam day.
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div v-dark class="readiness-report__indicator">
                                    <div v-dark class="readiness-report__score-container">
                                        <div class="readiness-report__non-numeric-score">
                                            {{ readinessLevel }}
                                        </div>
                                        <div v-dark class="readiness-report__non-numeric-subtext">
                                            READINESS
                                        </div>
                                    </div>
                                    <ReadinessScale
                                        class="readiness-report__scale readiness-report__scale--non-numeric"
                                        :readinessScore="readinessScoreScaled"
                                        :mode="'non-numeric'"
                                    />
                                    <div class="readiness-report__score-description">
                                        Based on your current study progress, our data suggests you would have a
                                        <span class="readiness-report__likelihood-text">
                                            {{ passingLikelihood }} likelihood of passing
                                        </span>
                                        the {{ currentExamName }} if you sat for the exam today.
                                    </div>
                                </div>
                            </template>
                            <div v-dark class="readiness-report__factors">
                                <PasserInsights v-if="isPasserInsightReport" />
                                <template v-else>
                                    <div class="readiness-report__factors-explanation">
                                        <div class="readiness-report__report-nudge">
                                            <span class="readiness-report__nudge-text" v-html="nudge" />
                                        </div>
                                        <div class="readiness-report__divider" v-dark />
                                        <div class="readiness-report__factors-explanation-head">
                                            Your Exam Readiness Factors
                                        </div>
                                        <div class="readiness-report__factors-explanation-body">
                                            Our model includes over 50 factors to analyze your study patterns
                                            and performance against data from thousands of Pocket Prep
                                            {{ currentExamName }} users who passed their exams. These are some of
                                            the key factors that most significantly impact your readiness.
                                        </div>
                                    </div>
                                    <div class="readiness-report__generic-factors-image-container">
                                        <img
                                            v-if="isDarkMode && isMobile"
                                            class="readiness-report__generic-factors-image"
                                            src="@/assets/readiness/generic-factors/graph-dm-mobile.png"
                                        />
                                        <img
                                            v-else-if="!isDarkMode && isMobile"
                                            class="readiness-report__generic-factors-image"
                                            src="@/assets/readiness/generic-factors/graph-lm-mobile.png"
                                        />
                                        <img
                                            v-else-if="isDarkMode && !isMobile"
                                            class="readiness-report__generic-factors-image"
                                            src="@/assets/readiness/generic-factors/graph-dm-desktop.png"
                                        />
                                        <img
                                            v-else-if="!isDarkMode && !isMobile"
                                            class="readiness-report__generic-factors-image"
                                            src="@/assets/readiness/generic-factors/graph-lm-desktop.png"
                                        />
                                    </div>
                                    <div class="readiness-report__factors-disclaimer">
                                        Your Pocket Prep Readiness Score reflects your performance and study
                                        patterns within our app. This assessment is not a guarantee of exam
                                        performance; only you know your complete study journey.
                                        Consider your own instincts as you continue working toward exam day.
                                    </div>
                                </template>
                                <div
                                    v-if="isPasserInsightReport"
                                    class="readiness-report__insights-nudge"
                                >
                                    <span class="readiness-report__nudge-text" v-html="nudge" />
                                </div>
                                <PocketLink
                                    v-else
                                    :is-dark-mode="isDarkMode"
                                    :href="`https://www.pocketprep.com/exam-readiness-report-information?${
                                        buildUTMParamString({
                                            utm_campaign: 'app_settings',
                                            utm_content: 'readiness_model',
                                        })
                                    }`"
                                    target="_blank"
                                    class="readiness-report__readiness-model-link"
                                    aria-label="More about our Model. Open link in browser view."
                                    @mousedown.prevent="aboutModelClicked"
                                >
                                    More about our Model
                                    <Icon class="readiness-report__external-link-icon" type="externalLink" />
                                </PocketLink>
                            </div>
                        </div>
                        <ReadinessReportSurvey
                            class="readiness-report__survey"
                            :factor-counts="factorCounts"
                            :is-passer-insight-report="isPasserInsightReport"
                            @close="emit('close')"
                        />
                    </template>
                </Modal>
            </template>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.readiness-report {
    color: $brand-black;
    z-index: 9998;

    &__modal {
        background-color: $floral-white;
        width: 1010px;
        height: calc(80vh - 56px);
        min-height: 640px;
        padding: 35px 25px 22px;
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;
        max-width: 95%;
        box-sizing: border-box;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: center;

        @include breakpoint(black-bear) {
            top: 0;
            left: 0;
            width: 100%;
            max-width: 100%;
            padding: 16px 18px 66px;
            transform: none;
            border-radius: 0;
            height: 100%;
        }

        &--dark {
            background-color: $moonlit-ocean;
        }
    }

    &__title {
        font-size: 26px;
        font-weight: 600;
        line-height: 34px;
        text-align: center;
        outline: none;
        display: flex;
        align-items: center;
        position: relative;

        @include breakpoint(black-bear) {
            font-size: 16px;
            line-height: 22px;
            padding: 4px 0;
        }
    }

    &__title-divider {
        height: 1px;
        width: 100%;
        background-color: $beach-sand;
        position: absolute;
        top: 61px;

        &--dark {
            background-color: rgb($beach-sand, 0.3);
        }
    }

    &__beta-tag {
        margin-left: 10px;
    }

    &__content {
        width: 392px;
        margin: 0 auto 30px;

        @include breakpoint(black-bear) {
            max-width: 339px;
        }
    }

    &__indicator {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 30px 16px 24px;
        border-bottom: 1px solid $beach-sand;

        &--dark {
            border-color: rgba($buttermilk, 0.2);
        }

        @include breakpoint(black-bear) {
            padding: 29px 0 24px;
        }
    }

    &__score-container {
        width: 280px;
        height: 147px;
        padding-top: 33px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: $white;
        border-radius: 147px 147px 0 0;
        position: relative;

        &--dark {
            background-color: $pickled-bluewood;
        }
    }

    &__score {
        font-size: 40px;
        font-weight: 700;
        letter-spacing: -2px;
        height: 44px;
        margin-bottom: 8px;
    }

    &__confidence {
        font-size: 20px;
        font-weight: 600;
        line-height: 27px;

        @include breakpoint(black-bear) {
            font-size: 17px;
            line-height: 25px;
            letter-spacing: -0.1px;
        }
    }

    &__non-numeric-score {
        font-size: 40px;
        font-weight: 700;
        margin-top: 12px;
    }

    &__non-numeric-subtext {
        font-size: 11px;
        font-weight: 700;
        line-height: 22px;
        letter-spacing: 1px;
        color: $steel;

        &--dark {
            color: $fog;
        }
    }

    &__scale {
        margin-top: -12px;
        margin-bottom: 36px;
        z-index: 1;
        max-width: 100%;
        width: 351px;

        @include breakpoint(black-bear) {
            width: 307px;
        }

        &--non-numeric {
            width: 360px;
        }
    }

    &__score-description {
        text-align: center;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;

        @include breakpoint(black-bear) {
            font-size: 16px;
            line-height: 22px;
        }
    }

    &__likelihood-text {
        font-weight: 700;
    }

    &__factors {
        padding: 24px 0;
        border-bottom: 1px solid $beach-sand;
        position: relative;

        &--dark {
            border-color: rgba($buttermilk, 0.2);
        }

        @include breakpoint(black-bear) {
            padding: 24px 0;
        }
    }

    &__factors-title {
        font-size: 20px;
        font-weight: 600;
        line-height: 27px;
        margin-bottom: 8px;

        @include breakpoint(black-bear) {
            font-size: 17px;
            line-height: 25px;
            letter-spacing: -0.1px;
        }
    }

    &__factors-list {
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        margin-bottom: 24px;
    }

    &__factor {
        margin-bottom: 4px;
    }

    &__other-factors {
        margin-top: 8px;
        color: $ash;
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;

        &--dark {
            color: $fog;
        }
    }

    &__external-link-icon {
        width: 13px;
        height: 11px;
        margin-left: 4px;
    }

    &__suggestions {
        padding: 24px 16px;
        border-bottom: 1px solid $beach-sand;

        &--dark {
            border-color: rgba($buttermilk, 0.2);
        }
    }

    &__suggestions-title {
        font-size: 20px;
        font-weight: 600;
        line-height: 27px;
        height: 24px;
        margin-bottom: 8px;

        @include breakpoint(black-bear) {
            font-size: 17px;
            line-height: 25px;
            letter-spacing: -0.1px;
        }
    }

    &__suggestions-description {
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        margin-bottom: 24px;
    }

    &__email-suggestions-btn {
        margin-bottom: 6px;
    }

    &__readiness-model-link {
        margin-left: 15px;
    }

    &__fine-print {
        padding: 24px 16px;
        color: $slate;
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;

        &--dark {
            color: $fog;
        }

        @include breakpoint(black-bear) {
            padding: 24px 0;
        }
    }

    &__survey {
        @include breakpoint(black-bear) {
            width: 335px;
        }
    }

    &__loading-spinner {
        margin-top: 48px;
        height: 25px;
        width: 25px;
    }

    &__error {
        margin-top: 48px;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-top: 6px;
    }

    &__error-icon {
        height: 21px;
        width: 21px;
    }

    &__error-head {
        font-size: 15px;
        line-height: 20px;
        margin-top: 23px;
        font-weight: 600;
    }

    &__error-description {
        color: $ash;
        font-size: 14px;
        line-height: 19px;
        margin-top: 8px;
        text-align: center;
        max-width: 260px;

        &--dark {
            color: $fog;
        }
    }

    &__v1-factors {
        padding: 0 16px;
    }

    &__factors-explanation {
        padding: 0 15px;
    }

    &__factors-explanation-head {
        font-size: 20px;
        font-weight: 600;
        line-height: 27px;
    }

    &__factors-explanation-body {
        margin-top: 6px;
        font-size: 16px;
        line-height: 22px;

        @include breakpoint(black-bear) {
            margin-top: 8px;
        }
    }

    &__factors-disclaimer {
        margin-bottom: 26px;
        font-size: 16px;
        line-height: 22px;
        padding: 0 15px;
    }

    &__generic-factors-image-container {
        margin-top: 24px;
        margin-bottom: 41px;
        display: flex;
        justify-content: center;

        @include breakpoint(black-bear) {
            margin-top: 31px;
            margin-bottom: 53px;
        }
    }

    &__generic-factors-image {
        height: 443.18px;
        margin-left: -18px;

        @include breakpoint(brown-bear) {
            margin-left: 0;
            height: 420px;
        }

        @include breakpoint(black-bear) {
            height: auto;
            width: 100vw;
            margin-left: 0;
        }
    }

    &__insight-head {
        text-align: center;
        margin-top: 54px;
        line-height: 24px;
        margin-bottom: 18px;

        @include breakpoint(black-bear) {
            margin-top: 50px;
        }
    }

    &__insight-title {
        font-weight: 600;
        line-height: 24px;
        margin-bottom: 12px;

        @include breakpoint(black-bear) {
            font-size: 18px;
            width: 309px;
        }
    }

    &__insight-description {
        @include breakpoint(black-bear) {
            padding: 0 15px;
        }
    }

    &__passer-insights {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
    }

    &__highlight-container {
        display: inline-block;
        position: relative;
    }

    &__highlight {
        position: absolute;
        color: $beach-sand;
        width: calc(100% + 4px);
        height: 80%;
        left: -2px;
        bottom: 0;
        transform: rotate(-9deg);
        z-index: -1;

        &--dark {
            color: rgb($butterscotch, 0.2);
            z-index: 0;
        }
    }

    &__insights-nudge {
        margin-top: 42px;
        font-size: 16px;
        line-height: 24px;

        :deep(strong) {
            font-weight: 600;
        }
    }

    &__report-nudge {
        font-size: 16px;
        line-height: 24px;
        margin-bottom: 24px;

        :deep(strong) {
            font-weight: 600;
        }
    }

    &__divider {
        width: 100%;
        height: 1px;
        background-color: $beach-sand;
        margin-bottom: 24px;

        &--dark {
            background-color: rgb($buttermilk, 0.2);
        }
    }
}
</style>
